import { ApiClient } from "@hakili/api-client";
import {
  type DealerDocumentsAnswer,
  type DealerReportAnswer,
  type DealerVehicleAnswer,
  type DealerVehicleRow,
  type DealerVehiclesAnswer,
  type NotificationRow,
  type NotificationsAnswer,
  type PhotoAttachedAnswer,
  type ReadAllAnswer,
  type ReadMarkAnswer,
  type TeamAnswer,
  type TeamMember as TeamRow,
  type UploadTicket,
  type VehiclePhoto as ContractPhoto,
  type VehicleInterestAnswer,
  type VehiclePhotosAnswer,
  type VehiclePriceChangeBody,
  vehiclePriceChangedAnswerReadSchema,
} from "@hakili/contracts";
import type { EquipmentReference } from "../routes/equipment.js";
import type { PhotoViews } from "../routes/photo-views.js";
import { createQueryClient, identityFrom, type QueryClient } from "@hakili/app-shell";
import { t } from "@hakili/i18n";

import type { CapacityEstimateDoor } from "./capacity-estimate.js";
import { readDealerDocumentsDoor } from "./dealer-documents.js";
import { type DealerDossierDetailRead, readDealerDossierDetail } from "./dealer-dossier-detail.js";
import { type DealerDossiersReadAnswer, readDealerDossiers } from "./dealer-dossiers.js";
import { type PriceRefusal, priceRefusalFrom } from "./price-refusal.js";
import { type DealerSettlement, dealerSettlementsFrom } from "./settlements.js";
import type {
  DossierProformas,
  DossierRequest,
  DossierRequestDoors,
  DossierRequestListing,
  IssuedProforma,
  PaperKind,
  VehiclePaperDoors,
  VehiclePapers,
} from "./vehicle-papers.js";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `P0-DLW-001`: the session gate, in `apps/console-web/src/session/useSession.ts`'s
 * shape and for the same reasons.
 *
 * ## What the gate decides
 *
 * Three states, never two: **`attente`** while the first `/me` is in flight,
 * **`connecte`**, and **`anonyme`**. Collapsing the first into the third is what
 * makes a shell flash its login screen for a moment before the session resolves,
 * and a person who sees a login screen types into it.
 *
 * ## The identity comes from `/me` and never from the login answer
 *
 * A login returns a token; the ORGANISATION comes from `/me`, which is the
 * route every shell boots on. A shell that trusted a login payload would carry
 * one truth at sign-in and another after a reload.
 */

export type SessionStatus = "attente" | "connecte" | "anonyme";

/**
 * `A-DLR`, Arc B: one notice of the dealer's, as `GET /me/notifications` answers it , DERIVED from the contract's row
 * (`CONTRACT-NOTIFICATIONS`), the five fields the bell reads, never a shape of this app's own.
 */
export type DealerNotice = Pick<NotificationRow, "id" | "title" | "body" | "linkPath" | "readAt">;

export interface DealerIdentity {
  readonly fullName: string;
  readonly email: string;
  readonly organisation: string | null;
  /** `P0-DLW-004`: the surface admits one kind of organisation, and a NAME cannot answer that. */
  readonly organisationType: string;
  /**
   * `P0-DLW-006`: this person's own id, which the team screen needs for two
   * things it must not guess , the default reassignment target (6.7), and
   * telling the manager's own row apart from everybody else's.
   */
  readonly id: string;
  /**
   * `round F`, `F1`: this person's own role, verbatim from `/me`.
   *
   * The rail says who is signed in , *« avec une équipe, deux personnes ouvrent
   * le même espace et doivent se reconnaître d'un coup d'oeil »*
   * (`hakili-dealer.jsx:1105-1108`) , and « nom · rôle » is what it says. The
   * IDENTIFIER travels; the shell maps it to a word, and an unknown role is
   * shown as itself rather than as a default label.
   */
  readonly role: string;
  /**
   * `P0-DLW-006`: the roles this principal may invite, from
   * `identity.roles.may_invite_roles` by way of `/me`.
   *
   * **The screen must not know which role it may create.** I16 decides it, by
   * data, and a form that hard-coded `employeConcession` would be the second
   * place that decision lives.
   */
  readonly mayInviteRoles: readonly string[];
  /**
   * `P0-DLW-007`: the rights `/me` answers, right key to level.
   *
   * The founder's walk of 5 September, step 6: the navigation offered « Équipe »
   * to an employee, who pressed it and read the door's refusal. The refusal was
   * honest; the MENU had not read. A navigation that offers a door the caller
   * cannot open teaches a new colleague that the product is broken on his first
   * afternoon.
   */
  readonly rights: Readonly<Record<string, string>>;
}

/** One row of `GET /api/v1/users`, as `users.controller.ts` `list` selects it. */
export interface TeamMember {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly deactivatedOn: string | null;
  /**
   * `round F5`: **the two facts `EquipeScreen` draws beside every row.**
   *
   * « Dans l'équipe depuis le … » is the row's own `created_at` and « dernière
   * connexion » its `last_login_at`. Neither can be supplied by a screen ,
   * `A50`: a browser's clock knows when the page was opened, not when an
   * account was made , and the door has always had the second.
   */
  readonly createdAt: string | null;
  readonly lastLoginAt: string | null;
  /**
   * The pending invitation this row is waiting on, where it is waiting on one.
   *
   * « Renvoyer » needs an identifier from another table, and a front that
   * guessed it would be guessing an identifier. `null` on everybody who has
   * accepted, which is what makes the control appear on exactly the rows it
   * belongs to.
   */
  readonly invitationId: string | null;
}

/** `GET /api/v1/users/{id}/deactivation`, `DeactivationDto` verbatim. */
/** `round F6`: one car a leaving person holds, as the removal dialog lists it. */
export interface HeldVehicle {
  readonly id: string;
  readonly stockReference: string;
  readonly title: string;
  readonly stage: string;
}

export interface Deactivation {
  readonly allowed: boolean;
  readonly openFiles: number | null;
  readonly reason: string | null;
  /**
   * `round F6`, `OD-16`: **what this person follows, REPORTED and not
   * enforced.**
   *
   * An open dossier blocks a removal because nobody has decided where it goes;
   * a car has somewhere to go by law (6.7), so the door lists them and the
   * dialog says what will move and where. Absent on an older door, and an older
   * door's absence is an empty list , not an unknown.
   */
  readonly vehicles: readonly HeldVehicle[];
}

/**
 * What a door answered, or the server's own sentence about why it did not.
 *
 * The API's French is shown as written , « Sortie impossible : 3 dossiers
 * ouverts doivent d'abord être transférés. » tells a manager what to do and a
 * generic failure does not. `P0-CNW-001` made the same choice for the same
 * reason.
 */
/**
 * `P0-PTA-001`: the dealership itself, as `GET /api/v1/me/dealership` answers.
 *
 * **`city` and `district` are `null` until somebody sets them**, and they are
 * `null` rather than `""` on purpose: an empty string is a value a person
 * chose, and the screen must be able to tell "not set" from "set to nothing".
 * The four dealerships Hakili has already opened have none, and the profile
 * says « Adresse à compléter » in words rather than rendering an empty line.
 */
/**
 * `P0-CAT-023` and `I4`: a car as the inventory renders one.
 *
 * **`sellerGrade` is deliberately absent.** It is `partnerships`' computed
 * figure and does not exist yet; the slot is left empty on the screen rather
 * than invented (`A48`), and a field here would invite somebody to fill it.
 */
export type InventoryVehicle = DealerVehicleRow & {
  /**
   * Derived from `publishedAt` by {@link withPublication}, never sent: `GENERIC-DOOR-SWEEP`, Arc 3 (A48) made the row
   * `@hakili/contracts`' {@link DealerVehicleRow}, the type `GET /me/vehicles` is returned by and an integration spec parses.
   */
  readonly published: boolean;
};

/**
 * `round F4`: the one place a car becomes « published » for a screen.
 *
 * Both doors send `publishedAt` and neither sends a boolean. Every screen that
 * asks *is this car on line* asks this function, so the list and the car's own
 * page cannot answer differently , which is precisely what they did.
 */
export function withPublication<T extends { readonly publishedAt: string | null }>(
  row: T,
): T & { readonly published: boolean } {
  return { ...row, published: row.publishedAt !== null };
}

/** One photograph of a car, with a URL signed at read time and never stored. */
export type VehiclePhoto = ContractPhoto;

/**
 * What `GET /api/v1/me/vehicles` answers: the cars, and the SERVER's instant.
 *
 * `OD-17` as ruled , no front application owns a clock and 09 §9.2.4 `D2` stays
 * as written, so the instant rides the door. « en ligne depuis N jours » is
 * `asOf` minus `listedOn`, two facts the server sent, and never the laptop's
 * own clock , which on a dealership's machine is not a fact Hakili should
 * depend on for a number it shows that dealership.
 */
export interface Inventory {
  readonly vehicles: readonly InventoryVehicle[];
  readonly asOf: string;
}

/** One car, with the journal `WF-DLR-03` renders under its stepper. */
/**
 * The car's own fiche, as `GET /me/vehicles/{id}` sends it plus the derived `published`. **It is NOT a list row**: the fiche
 * carries no `photoCount`, `priceDrop`, `expertiseReportId`, `hasBuyer` or `reportVerdict`, which the
 * previous `extends InventoryVehicle` claimed it did.
 */
export type VehicleDetail = DealerVehicleAnswer & { readonly published: boolean };

/**
 * `P0-CAT-030`, round F4: the makes the add panel offers, and the escape.
 *
 * The models ride with their make in one answer: the whole reference is a few
 * kilobytes, and a round trip per make would put a spinner inside a select on
 * a handset in Bamako.
 */
export interface MakeReference {
  readonly makes: readonly { readonly name: string; readonly models: readonly string[] }[];
  /**
   * The value that means « Autre ». It never reaches a column: it tells the
   * panel to open a text box, and what the dealer types is what the car
   * carries. The mirror of a photograph's view, which IS stored.
   */
  readonly otherMake: string;
}

/** `P0-CAT-030`: the gearbox and the fuel, key and label held apart. */
export interface VehicleOptions {
  readonly gearboxes: readonly { readonly key: string; readonly label: string }[];
  readonly fuels: readonly { readonly key: string; readonly label: string }[];
}

/**
 * `round T-1d`: **the balance as the ledger's sum**, read through the dealer's
 * own door (`GET /api/v1/me/balance`). Every figure here is the api's: the sum,
 * the lines, the top-ups awaiting Hakili's confirmation, and the two totals
 * that stay `null` until their doors stand (R81: a dash, never a zero).
 */
export interface BalanceMovement {
  readonly id: string;
  readonly reference: string;
  readonly label: string;
  readonly amount: number;
  readonly reason: string;
  readonly vehicleId: string | null;
  readonly vehicleLabel: string | null;
  readonly movedOn: string;
}

export interface PendingTopup {
  readonly id: string;
  readonly amount: number;
  readonly transferReference: string;
  readonly declaredAt: string;
}

export interface HakiliAccount {
  readonly bankName: string | null;
  readonly accountName: string | null;
  readonly accountNumber: string | null;
}

export interface DealerBalance {
  readonly balance: number;
  readonly movementCount: number;
  readonly lastMovementOn: string | null;
  readonly movements: readonly BalanceMovement[];
  readonly pendingTopups: readonly PendingTopup[];
  readonly upcomingAdvancesTotal: number | null;
  /** `T-2c`, `M2 (5)`: WF-CON-38 signal 7 , the threshold and the fact, both the door's. */
  readonly lowThreshold: number | null;
  readonly isLow: boolean;
  readonly openClaimsTotal: number | null;
  readonly hakiliAccount: HakiliAccount | null;
  /** The dealer's own rate, T-1c's two layers, for the notice's figures. */
  readonly commission: { readonly ratePct: number; readonly source: "convention" | "standard" };
}

export interface Dealership {
  readonly name: string;
  readonly city: string | null;
  readonly district: string | null;
  readonly phone: string | null;
  /**
   * `A-DLR` Arc C, `C0`: the four facts the dealership's proforma prints, as its manager entered them , `null`
   * until he has (the issue door then refuses, naming each missing one).
   */
  readonly rccm: string | null;
  readonly nif: string | null;
  readonly paymentTerms: string | null;
  readonly warranty: string | null;
  /**
   * `round K-2` (resumed), `M3`/`M4`: what the platform holds against this dealership, derived by
   * the door , the demotion's end (a breach within the published days) and the suspension by rule.
   * `null` and `false` while nothing stands; the home prints one banner each (R81).
   */
  readonly demotedUntil: string | null;
  readonly suspended: boolean;
}

/** `T-2`, M0 (c): the dealership's account details as its responsable entered them. */
export interface DealerBankAccount {
  readonly version: number;
  readonly bankName: string;
  readonly holderName: string;
  readonly accountNumber: string;
  readonly enteredAt: string;
}

export type DoorResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly message: string;
      /**
       * `round F5`: **which field the door refused, when it said.**
       *
       * `4.5.5` puts the field in `details`, and the api has always sent it ,
       * « Ce quartier n'est pas reconnu. » arrives with `field: "district"`.
       * The front dropped it on the floor and printed the sentence at the top
       * of a long form, so a person reading it had to guess which of eleven
       * fields it was about. The founder's walk of 8 September met exactly
       * that: a `400` about a quartier, shown nowhere near the quartier.
       *
       * Absent where the door named no field , a conflict about the car as a
       * whole, a connection that never arrived , and a screen shows those where
       * it always did.
       */
      readonly field?: string;
      /** `G-D1`: the price door's `409` details (the ceiling, the lock), carried beside the sentence; absent from every other door. */
      readonly refusal?: PriceRefusal;
    };

/** `PRICE-GAP-DEALER`, M3: one act waiting for the dealership's hand. */
export interface AttentionItem {
  readonly kind: "miseADisposition" | "ecartPrix";
  readonly vehicleId: string;
  readonly stockReference: string;
  readonly title: string;
  readonly since: string;
}

/** `PRICE-GAP-DEALER`, M1: the gap standing on a car's open file, with the seller's answer once given. */
export interface PriceGapView {
  readonly dossierReference: string;
  readonly expertisedValue: number;
  readonly ceiling: number;
  readonly currentPrice: number;
  readonly answer: {
    readonly decision: "aligne" | "propose" | "maintenu";
    readonly price: number;
    readonly decidedAt: string;
  } | null;
}

export interface DealerSession {
  readonly status: SessionStatus;
  readonly identite: DealerIdentity | null;
  readonly queryClient: QueryClient;
  readonly connecter: (identifiants: { email: string; password: string }) => Promise<void>;
  readonly deconnecter: () => void;
  /** The boot read, so the entry starts it once rather than on every render. */
  readonly lireMoi: () => Promise<void>;
  /**
   * `P0-DLW-002`: takes the session the ACCEPT door opened.
   *
   * Accepting an invitation is a login , the door sets the password, activates
   * the account, opens a session and returns its access token, all in one
   * transaction. A screen that threw that token away would send a person who
   * had just chosen a password to a form asking for it.
   */
  readonly adopterSession: (accessToken: string) => void;
  /** `R64`: the covered cities, so a form chooses instead of accepting typing. */
  readonly listCities: () => Promise<DoorResult<readonly { id: string; name: string }[]>>;
  /** `R64`: the places inside one city. */
  readonly listCityZones: (cityId: string) => Promise<DoorResult<readonly { code: string; label: string }[]>>;
  /** `I4`: this dealership's cars, self-scoped. */
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
  /** `PROSPECTS-INTEREST` (`P1-CAT-051`): how many buyers saved ONE of this dealership's cars; a count, no identity. */
  readonly readVehicleInterest: (vehicleId: string) => Promise<DoorResult<VehicleInterestAnswer>>;
  /**
   * `F2`, the founder's reading: the equipment SUGGESTIONS and their bounds.
   *
   * Read from the door for the same reason the views are, and NOT for the same
   * reason it enforces them: these six are suggestions, not a vocabulary, and
   * the two numbers beside them are the api's rule read once rather than
   * copied into a screen that would drift from it.
   */
  readonly listEquipment: () => Promise<DoorResult<EquipmentReference>>;
  /**
   * `F2.3`: the photograph views the api owns.
   *
   * Read from the door and never transcribed: the names are wire values the
   * upload door compares, and a copy in a screen is a copy that drifts.
   */
  readonly listPhotoViews: () => Promise<DoorResult<PhotoViews>>;
  /** `F2.2`: a car's photographs, in order, each with a freshly signed URL. */
  readonly listPhotos: (
    vehicleId: string,
  ) => Promise<DoorResult<{ photos: readonly VehiclePhoto[]; missingRecommended: readonly string[] }>>;
  /**
   * 06 §6.3.3's three calls, as ONE gesture.
   *
   * The panel hands a file and a view; this tickets, PUTs the bytes to the
   * store with the headers the door returned VERBATIM, and confirms. A caller
   * that built its own headers would be sending its own idea of the contract,
   * and the bucket answers a 403 that never reaches this application.
   */
  readonly uploadPhoto: (input: {
    vehicleId: string;
    view: string;
    file: File;
  }) => Promise<DoorResult<{ id: string }>>;
  readonly removePhoto: (vehicleId: string, photoId: string) => Promise<DoorResult<void>>;
  /** `A-DLR`, Arc A: the vehicle's own papers (carte grise, visite technique) and its chassis number. */
  /** `A-DLR`, Arc B: « Pièces demandées » on a car, and the dealer's own deposit of his dossier's proforma. */
  readonly readRequests: DossierRequestDoors["readRequests"];
  readonly issueProforma: DossierRequestDoors["issueProforma"];
  readonly uploadProforma: DossierRequestDoors["uploadProforma"];
  /** `A-DLR`, Arc B (S4-6): the dealer's notices , the rows the door answers, read by his own act. */
  readonly notices: readonly DealerNotice[];
  /** `unread` as the door serves it. */
  readonly unreadNotices: number;
  readonly readNotice: (id: string) => void;
  /** `POST /api/v1/me/notifications/read` (`MARK-ALL-READ`, #743): all her unread notices read by ONE act; the badge is what the door re-derived. */
  readonly readAllNotices: () => void;
  readonly readPapers: VehiclePaperDoors["readPapers"];
  readonly attachPaper: VehiclePaperDoors["attachPaper"];
  readonly declareChassis: VehiclePaperDoors["declareChassis"];
  /** `WF-DLR-02`'s publish, with its « at least one photo » refused in words. */
  readonly publishVehicle: (vehicleId: string) => Promise<DoorResult<{ publishedAt: string }>>;
  /** `WF-DLR-03`: one car and its journal. */
  readonly readVehicle: (id: string) => Promise<DoorResult<VehicleDetail>>;
  /** `P0-CAT-030`: the makes and their models, and the key that means « Autre ». */
  readonly listVehicleMakes: () => Promise<DoorResult<MakeReference>>;
  /** `P0-CAT-030`: the gearbox and the fuel, as the api spells them. */
  readonly listVehicleOptions: () => Promise<DoorResult<VehicleOptions>>;
  /**
   * `P0-CAT-033`: the description edit. Never the price, which has its own
   * door, its own append-only history and its own journal line.
   */
  readonly editVehicle: (
    id: string,
    input: Record<string, unknown>,
  ) => Promise<DoorResult<{ changed: boolean }>>;
  /** `P0-CAT-031`: « Modifier le prix ». The refusal is the api's own sentence. */
  /** `PRICE-GAP-DEALER`, M3: what waits for this dealership's hand (`GET me/attention`); empty on a refusal. */
  readonly readAttention: () => Promise<DoorResult<readonly AttentionItem[]>>;
  /** `PRICE-GAP-DEALER`, M1: the gap on a car's open file, or `null`. */
  readonly readPriceGap: (vehicleId: string) => Promise<DoorResult<PriceGapView | null>>;
  /** `PRICE-GAP-DEALER`, M1: the seller's answer , align, propose (with a price) or maintain. */
  readonly answerPriceGap: (
    vehicleId: string,
    answer:
      { readonly kind: "align" | "maintain" } | { readonly kind: "propose"; readonly priceAmount: number },
  ) => Promise<DoorResult<{ decision: string; previousAmount: number; priceAmount: number }>>;
  readonly changePrice: (
    id: string,
    priceAmount: number,
  ) => Promise<DoorResult<{ priceAmount: number; previousAmount: number }>>;
  /** `P0-CAT-032`: « Retirer du catalogue », to the terminal branch. */
  readonly withdrawVehicle: (id: string) => Promise<DoorResult<{ stage: string }>>;
  /** `P0-CAT-032`: « Remettre en ligne », which republishes in the same act. */
  readonly relistVehicle: (id: string) => Promise<DoorResult<{ stage: string }>>;
  /** `P0-CAT-023`: « Mettre en ligne ». Refusals are the api's own words. */
  readonly createVehicle: (input: Record<string, unknown>) => Promise<DoorResult<{ id: string }>>;
  /** The next reference in this dealer's own series, proposed and editable. */
  readonly nextReference: () => Promise<DoorResult<string>>;
  /** `P0-PTA-001`: this dealership, self-scoped , no identifier in the path. */
  readonly readDealership: () => Promise<DoorResult<Dealership>>;
  /** `T-1d`, WF-DLR-13: the balance and its movements, self-scoped like the dealership. */
  readonly readBalance: () => Promise<DoorResult<DealerBalance>>;
  /**
   * `DEALER-PREQUAL` (round A-D1): the dealer's capacity-estimate door (`POST /api/v1/me/capacity-estimate`, `P1-SIM-017`, #904), declared in the generated operations and wired below.
   */
  readonly estimateCapacity: CapacityEstimateDoor;
  /** `T-2` (WF-DLR-11): the dealer's settlements, his own leg declared, the non-receipt, the handover, his account. */
  readonly readSettlements: () => Promise<DoorResult<readonly DealerSettlement[]>>;
  /** `T-2b`, `M1` (WF-DLR-07): the control's checklist and the advance the door will debit, by the dealer's own door. */
  readonly readAvailability: (vehicleId: string) => Promise<DoorResult<unknown>>;
  /** Lane E, M2: the report standing on the dealer's car, its two documents (never the buyer). */
  readonly readReport: (vehicleId: string) => Promise<DoorResult<DealerReportAnswer>>;
  /** « Confirmer la mise à disposition » , once, with an `Idempotency-Key`; the advance debited by `payments` as the consumer. */
  readonly confirmAvailability: (vehicleId: string) => Promise<DoorResult<unknown>>;
  /**
   * `idempotencyKey` is minted by the caller when it must live across attempts (the dossier list's dialog: one key per opening, so a double tap
   * confirms once); left out, each call mints its own as it always did.
   */
  readonly confirmReceipt: (
    settlementId: string,
    receivedOn: string,
    idempotencyKey?: string,
  ) => Promise<DoorResult<unknown>>;
  /** `P1-DLW-048` (G-E1): the dealership's dossier list, `GET /api/v1/me/dealership/dossiers`, parsed with the provider's own READ schema (A48). */
  readonly readDossiers: () => Promise<DoorResult<DealerDossiersReadAnswer>>;
  /** `P1-DLW-049`: the pièces of one dossier of the dealership, `GET /api/v1/me/dealership/dossiers/{id}/pieces` (`P1-DOS-079`). */
  readonly readDocuments: (dossierId: string) => Promise<DoorResult<DealerDocumentsAnswer>>;
  /** `P1-DLW-052` (B5-b): one dossier of the dealership in detail, `GET /api/v1/me/dealership/dossiers/{id}`, parsed with the READ twin (`./dealer-dossier-detail.ts`). */
  readonly readDossierDetail: (dossierId: string) => Promise<DealerDossierDetailRead>;
  readonly reportNonReceipt: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly handoverVehicle: (vehicleId: string, voucherReference: string) => Promise<DoorResult<unknown>>;
  readonly readBankAccount: () => Promise<DoorResult<DealerBankAccount | null>>;
  readonly writeBankAccount: (input: {
    bankName: string;
    holderName: string;
    accountNumber: string;
  }) => Promise<DoorResult<DealerBankAccount>>;
  /**
   * `T-1d`, WF-DLR-13 step 3 as amended: « Recharger mon solde » DECLARES a
   * transfer the dealer made to Hakili's account , his amount, his reference ,
   * and Hakili confirms it on evidence; `Idempotency-Key` minted here.
   */
  readonly declareTopup: (input: {
    amount: number;
    transferReference: string;
  }) => Promise<DoorResult<PendingTopup>>;
  /** `P0-PTA-001`: the manager's write. The api refuses in its own words. */
  readonly writeAddress: (input: {
    city: string;
    district: string;
    phone: string;
  }) => Promise<DoorResult<Dealership>>;
  /** `A-DLR` Arc C, `C0`: the manager's write of what his proforma prints. The api refuses in its own words. */
  readonly writeProformaDetails: (input: {
    rccm: string;
    nif: string;
    paymentTerms: string;
    warranty: string;
  }) => Promise<DoorResult<Dealership>>;
  /** `P0-DLW-006`: the team, scoped by the api to this dealership and nothing else. */
  readonly listTeam: () => Promise<DoorResult<readonly TeamMember[]>>;
  /**
   * `round F6`, `OD-15`: **the manager hands one car to a colleague.**
   *
   * The founder's rule: *le responsable peut le confier à qui il veut à tout
   * moment*. The door refuses an employee on `gererEquipeConcession` before the
   * method runs, so this is never offered to one , and the refusal would be in
   * the api's own words if it were.
   */
  readonly assignVehicle: (
    id: string,
    userId: string,
  ) => Promise<DoorResult<{ id: string; fullName: string }>>;
  /** `P0-DLW-006`: invites one colleague, in the only role `may_invite_roles` allows. */
  readonly invite: (input: {
    fullName: string;
    email: string;
    role: string;
  }) => Promise<DoorResult<{ delivered: boolean }>>;
  /**
   * `round F5`, found work of round F: **« Renvoyer », for an invitation that
   * has not been accepted.**
   *
   * The door has existed since `P0-IDN-062` and was closed to the one person
   * who needs it: its layer 2 named `inviterPremierResponsable`,
   * `gererEquipeBanque` and `gererComptesHakili`, and a dealership's manager
   * holds none of them , he holds `gererEquipeConcession`. So the control could
   * not be built, and the drawing's own bullet , *« Tant qu'elle n'a pas
   * accepté, vous pouvez renvoyer l'invitation depuis la liste »* , described
   * something no screen could do.
   */
  readonly resendInvitation: (invitationId: string) => Promise<DoorResult<{ delivered: boolean }>>;
  /**
   * `OD-18`, closed at `round F5`: **« Une idée ? » sends a demand somewhere.**
   *
   * The rail's control was inert with its reason , `I19` , because
   * `product_feedback` had no route a dealership could call and a modal that
   * answered « Demande transmise » while transmitting nothing is the lie `R81`
   * exists to stop. `P1-UIK-025` is amended by record with both texts kept: a
   * control is live or says why it is not, and demand is recorded by a DOOR.
   *
   * The three answers and the SCREEN travel; who asked, in what role and for
   * which dealership is read by the door from the caller's own row, so nothing
   * here can name somebody else.
   */
  readonly submitFeatureRequest: (input: {
    goal: string;
    missing: string;
    benefit: string;
    screenLabel: string;
  }) => Promise<DoorResult<{ id: string }>>;
  /** `P0-DLW-006`: what a removal WOULD do, asked before the gesture is offered. */
  readonly readDeactivation: (userId: string) => Promise<DoorResult<Deactivation>>;
  /** `P0-DLW-006`: the removal itself. */
  readonly deactivate: (userId: string, reassignTo?: string) => Promise<DoorResult<void>>;
}

/**
 * The server's own sentence, or a fallback when the failure carried none.
 *
 * `round F3`: **a browser's network error is never shown to a person.**
 *
 * `fetch` rejects with a `TypeError` whose message is « Failed to fetch » , the
 * browser talking to itself. It says that for a CORS refusal, a DNS failure, a
 * dropped connection and a blocked mixed-content request alike, and it says it
 * in English on a French screen. The founder read exactly that line on
 * `concession-staging` and it told him nothing about what to do.
 *
 * A `TypeError` from `fetch` therefore falls through to the caller's own
 * sentence, which names the STEP that failed and what became of the car. The
 * real reason is in the console, where the browser put it, and the walk of
 * `upload-walk.spec.ts` reads it there.
 */
const messageOf = (failure: unknown, fallback: string): string => {
  if (failure instanceof TypeError) return fallback;
  const message = (failure as { message?: unknown } | null)?.message;
  return typeof message === "string" && message !== "" ? message : fallback;
};

/**
 * The field the door named, or nothing.
 *
 * `ApiError` already carries `details` , the envelope's own , so this reads
 * what the server sent rather than parsing a sentence. A refusal that named no
 * field returns `undefined`, and the screen shows it where it always did.
 */
const fieldOf = (failure: unknown): string | undefined => {
  const details = (failure as { details?: unknown } | null)?.details;
  const field = (details as { field?: unknown } | null)?.field;
  return typeof field === "string" && field !== "" ? field : undefined;
};

/** A refused door, with the field where the api named one. */
const refusalOf = (failure: unknown, fallback: string): { ok: false; message: string; field?: string } => {
  const field = fieldOf(failure);
  return { ok: false, message: messageOf(failure, fallback), ...(field === undefined ? {} : { field }) };
};

/**
 * One row of `GET /api/v1/users`, from the contract to this application's own names.
 *
 * The columns are the api's , `full_name`, `status`, `deactivated_on` , and a screen that read `fullName` off this
 * answer would be `A48`'s defect exactly: a client agreeing with itself about a shape the server never sent. The wire
 * row is now `@hakili/contracts`' {@link TeamRow} (`GENERIC-DOOR-SWEEP`, Arc 1): the door is typed by it and an
 * integration spec parses the door's real answer with it, so this mapping reads fields that exist and carries no
 * guess of its own.
 */
const memberFrom = (row: TeamRow): TeamMember => ({
  id: row.id,
  fullName: row.full_name,
  email: row.email,
  role: row.role,
  status: row.status,
  deactivatedOn: row.deactivated_on,
  createdAt: row.created_at,
  lastLoginAt: row.last_login_at,
  invitationId: row.invitation_id,
});

export function useSession(origin: string, fetchImpl?: typeof globalThis.fetch): DealerSession {
  const [status, setStatus] = useState<SessionStatus>("attente");
  const [identite, setIdentite] = useState<DealerIdentity | null>(null);

  const client = useRef<ApiClient | null>(null);
  client.current ??= new ApiClient({
    origin,
    // `P0-IDN-063`: which surface this application is, so the api names its
    // session's cookie for it and no other application can revive it.
    surface: "dealer",
    // The refresh itself failed, so the session is genuinely over. This is the
    // only place a silent refresh may end, and it ends at the login screen.
    onSessionEnded: () => {
      setIdentite(null);
      setStatus("anonyme");
    },
    ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
  });
  const api = client.current;

  const queries = useRef<QueryClient | null>(null);
  queries.current ??= createQueryClient({
    onSessionExpired: () => {
      setIdentite(null);
      setStatus("anonyme");
    },
  });

  const lireMoi = useCallback(async (): Promise<void> => {
    try {
      // `P0-DLW-005`: parsed by `identityFrom`, which is the CONTRACT's own
      // reader , `/me` answers `profile: { fullName, email }` and
      // `organisation: { id, name, type }`, and this hook read `me.fullName`
      // and `me.organisation` at the top level, so both were empty on every
      // path since the app existed. Its specs passed because their stub
      // invented a flat shape, which is `§5.33`: a fixture the contract did not
      // build is a witness quoting itself.
      const me = await api.request<unknown>("/api/v1/me", "get");
      const lu = identityFrom(me);
      if (lu === null) {
        // An answer this reader cannot parse is not a session. Saying anonymous
        // is honest; rendering a shell with empty fields is what happened
        // before, for a year, without a word.
        setIdentite(null);
        setStatus("anonyme");
        return;
      }
      setIdentite({
        id: lu.id,
        role: lu.role,
        fullName: lu.fullName,
        email: lu.email,
        organisation: lu.organisation === "" ? null : lu.organisation,
        organisationType: lu.organisationType,
        mayInviteRoles: lu.mayInviteRoles,
        rights: lu.rights,
      });
      setStatus("connecte");
    } catch {
      setIdentite(null);
      setStatus("anonyme");
    }
  }, [api]);

  const connecter = useCallback(
    async (identifiants: { email: string; password: string }): Promise<void> => {
      await api.request("/api/v1/auth/staff/login", "post", { body: identifiants });
      await lireMoi();
    },
    [api, lireMoi],
  );

  const adopterSession = useCallback(
    (accessToken: string): void => {
      api.setAccessToken(accessToken);
      // Then `/me`, because the ORGANISATION comes from `/me` and never from a
      // login answer , this hook's own header, and the accept door's answer is
      // a login answer like any other.
      void lireMoi();
    },
    [api, lireMoi],
  );

  /**
   * `P0-DLW-006`: the four doors the team screen calls, each answering the
   * server's own sentence when it refuses.
   *
   * **Through `ApiClient`**, so the refresh dance, the surface header and the
   * declared-path check all apply , and so a `401` that outlives one refresh
   * ends the session at the login screen rather than as a red box on a list.
   *
   * **A refusal is a value here and never a throw.** The screen has somewhere to
   * put a sentence , beside the row, in the dialog , and an exception would
   * unmount the shell and tell a manager their application is broken when the
   * truth is that a colleague still holds three files.
   */
  const listTeam = useCallback(async (): Promise<DoorResult<readonly TeamMember[]>> => {
    try {
      const answer = await api.request<TeamAnswer>("/api/v1/users", "get");
      const members = answer.data.map(memberFrom);
      return { ok: true, value: members };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  const invite = useCallback(
    async (input: {
      fullName: string;
      email: string;
      role: string;
    }): Promise<DoorResult<{ delivered: boolean }>> => {
      try {
        const answer = await api.request<{ delivered?: unknown }>("/api/v1/users", "post", {
          body: input,
        });
        // `delivered` absent means the api did not say, and the honest reading
        // is that it went , `FicheConcession` made the same call for the same
        // reason. The console must not invent a failure the api never reported.
        return { ok: true, value: { delivered: answer.delivered !== false } };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  const readDeactivation = useCallback(
    async (userId: string): Promise<DoorResult<Deactivation>> => {
      try {
        const answer = await api.request<Omit<Deactivation, "vehicles"> & { vehicles?: unknown }>(
          "/api/v1/users/{id}/deactivation",
          "get",
          { params: { id: userId } },
        );
        // `round F6`: an older door sends no `vehicles`, and absence is an
        // EMPTY list rather than an unknown , the F5 lesson, one field along.
        return {
          ok: true,
          value: {
            ...answer,
            vehicles: Array.isArray(answer.vehicles) ? (answer.vehicles as readonly HeldVehicle[]) : [],
          },
        };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  const deactivate = useCallback(
    async (userId: string, reassignTo?: string): Promise<DoorResult<void>> => {
      try {
        // `round F6`, 6.7: **the target rides the removal**, because the
        // account's closure and the cars' move are ONE act. The door refuses in
        // words when somebody holds cars and no target is named.
        await api.request("/api/v1/users/{id}/deactivate", "post", {
          params: { id: userId },
          ...(reassignTo === undefined ? {} : { body: { reassignTo } }),
        });
        return { ok: true, value: undefined };
      } catch (failure) {
        // The api's own sentence, as written: « Sortie impossible : 3 dossiers
        // ouverts doivent d'abord être transférés. » is what a manager needs.
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /**
   * The dealership every member may read. No right: the session already says
   * which dealership the caller belongs to, and the door is self-scoped , there
   * is no identifier in the path to get wrong.
   */
  const readDealership = useCallback(async (): Promise<DoorResult<Dealership>> => {
    try {
      const answer = await api.request<unknown>("/api/v1/me/dealership", "get");
      const row = answer as Record<string, unknown> | null;
      if (row === null || typeof row["name"] !== "string") {
        return { ok: false, message: t("common.error.reponseInattendue") };
      }
      const optional = (key: string): string | null => {
        const value = row[key];
        // `null` and not `""`: an empty string is a value somebody chose, and
        // the screens must be able to tell "not set" from "set to nothing".
        return typeof value === "string" && value !== "" ? value : null;
      };
      return {
        ok: true,
        value: {
          name: row["name"],
          city: optional("city"),
          district: optional("district"),
          phone: optional("phone"),
          rccm: optional("rccm"),
          nif: optional("nif"),
          paymentTerms: optional("paymentTerms"),
          warranty: optional("warranty"),
          demotedUntil: optional("demotedUntil"),
          suspended: row["suspended"] === true,
        },
      };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /**
   * The manager's write. The api refuses an uncovered city and an unrecognised
   * Bamako quartier **in its own words**, and those words are rendered , the
   * screen never composes a refusal it did not receive.
   */
  const writeAddress = useCallback(
    async (input: { city: string; district: string; phone: string }): Promise<DoorResult<Dealership>> => {
      try {
        await api.request<unknown>("/api/v1/me/dealership/address", "put", { body: input });
        // Awaited INSIDE the try, so a failure of the read-back is caught here
        // rather than escaping as a rejected promise the caller never guarded.
        return await readDealership();
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api, readDealership],
  );

  /**
   * `A-DLR` Arc C, `C0`: the manager's write of the four facts his proforma prints. The api's own sentence is
   * rendered on a refusal, as the address's is.
   */
  const writeProformaDetails = useCallback(
    async (input: {
      rccm: string;
      nif: string;
      paymentTerms: string;
      warranty: string;
    }): Promise<DoorResult<Dealership>> => {
      try {
        await api.request<unknown>("/api/v1/me/dealership/proforma-details", "put", { body: input });
        return await readDealership();
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api, readDealership],
  );

  /**
   * `T-1d`: the balance, read as the api answers it. Nothing is derived here:
   * the sum is the view's, the lines are the ledger's, and a `null` total is
   * rendered as a dash by the screen (R81).
   */
  const readBalance = useCallback(async (): Promise<DoorResult<DealerBalance>> => {
    try {
      const answer = (await api.request<unknown>("/api/v1/me/balance", "get")) as Record<
        string,
        unknown
      > | null;
      if (answer === null || typeof answer["balance"] !== "number" || !Array.isArray(answer["movements"])) {
        return { ok: false, message: t("common.error.reponseInattendue") };
      }
      const number = (value: unknown): number | null => (typeof value === "number" ? value : null);
      const text = (value: unknown): string | null => (typeof value === "string" ? value : null);
      const account = answer["hakiliAccount"] as Record<string, unknown> | null | undefined;
      const commission = (answer["commission"] ?? {}) as Record<string, unknown>;
      return {
        ok: true,
        value: {
          balance: answer["balance"],
          movementCount: number(answer["movementCount"]) ?? 0,
          lastMovementOn: text(answer["lastMovementOn"]),
          movements: (answer["movements"] as Record<string, unknown>[]).map((row) => ({
            id: String(row["id"]),
            reference: String(row["reference"]),
            label: String(row["label"]),
            amount: number(row["amount"]) ?? 0,
            reason: String(row["reason"]),
            vehicleId: text(row["vehicleId"]),
            vehicleLabel: text(row["vehicleLabel"]),
            movedOn: String(row["movedOn"]),
          })),
          pendingTopups: (Array.isArray(answer["pendingTopups"])
            ? (answer["pendingTopups"] as Record<string, unknown>[])
            : []
          ).map((row) => ({
            id: String(row["id"]),
            amount: number(row["amount"]) ?? 0,
            transferReference: String(row["transferReference"]),
            declaredAt: String(row["declaredAt"]),
          })),
          upcomingAdvancesTotal: number(answer["upcomingAdvancesTotal"]),
          lowThreshold: number(answer["lowThreshold"]),
          isLow: answer["isLow"] === true,
          openClaimsTotal: number(answer["openClaimsTotal"]),
          hakiliAccount:
            account === null || account === undefined
              ? null
              : {
                  bankName: text(account["bankName"]),
                  accountName: text(account["accountName"]),
                  accountNumber: text(account["accountNumber"]),
                },
          commission: {
            ratePct: number(commission["ratePct"]) ?? 0,
            source: commission["source"] === "convention" ? "convention" : "standard",
          },
        },
      };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** `T-1d`: the declaration. The refusal is the api's own sentence, field named. */
  const declareTopup = useCallback(
    async (input: { amount: number; transferReference: string }): Promise<DoorResult<PendingTopup>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/balance/topups", "post", {
          body: input,
          idempotencyKey: globalThis.crypto.randomUUID(),
        })) as { topup?: Record<string, unknown> } | null;
        const topup = answer?.topup;
        if (topup === undefined || typeof topup["id"] !== "string" || typeof topup["amount"] !== "number") {
          return { ok: false, message: t("common.error.reponseInattendue") };
        }
        return {
          ok: true,
          value: {
            id: topup["id"],
            amount: topup["amount"],
            transferReference: String(topup["transferReference"]),
            declaredAt: String(topup["declaredAt"]),
          },
        };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** `I4`: this dealership's cars, and the server's instant. Self-scoped. */
  const listVehicles = useCallback(async (): Promise<DoorResult<Inventory>> => {
    try {
      // `GENERIC-DOOR-SWEEP`, Arc 3 (A48): the answer is `@hakili/contracts`' {@link DealerVehiclesAnswer}, the type the
      // door is returned by and the schema an integration spec parses the door's real answer with.
      const answer = await api.request<DealerVehiclesAnswer>("/api/v1/me/vehicles", "get");
      return {
        ok: true,
        value: {
          vehicles: answer.data.map((row) => withPublication(row)),
          // The SERVER's instant, never the browser's clock: reading it here would be `OD-17`'s ruling undone in one line.
          asOf: answer.asOf,
        },
      };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** `PROSPECTS-INTEREST`: `GET /me/vehicles/{vehicleId}/interest`, the served `savedCount` of one car of this dealership. */
  const readVehicleInterest = useCallback(
    async (vehicleId: string): Promise<DoorResult<VehicleInterestAnswer>> => {
      try {
        const answer = await api.request<VehicleInterestAnswer>(
          "/api/v1/me/vehicles/{vehicleId}/interest",
          "get",
          {
            params: { vehicleId },
          },
        );
        return { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `F2`: the equipment suggestions and their bounds, from the api. */
  const listEquipment = useCallback(async (): Promise<DoorResult<EquipmentReference>> => {
    try {
      const answer = await api.request<unknown>("/api/v1/reference/equipment", "get");
      /**
       * **The SHAPE is checked, never asserted with a cast.**
       *
       * `as EquipmentReference` tells the compiler what to believe and tells
       * the browser nothing. A door answering `{ data: [] }` , which is what
       * every other list door in this api answers , reached the panel as an
       * object whose `suggested` was `undefined`, and the panel died on
       * `.length`. A crash is caught by the boundary and renders as a blank
       * screen: the one failure mode `A48` exists to make impossible.
       */
      const shape = answer as Partial<EquipmentReference> | null;
      return shape !== null &&
        Array.isArray(shape.suggested) &&
        typeof shape.maxEntries === "number" &&
        typeof shape.maxLength === "number"
        ? {
            ok: true,
            value: {
              suggested: shape.suggested,
              maxEntries: shape.maxEntries,
              maxLength: shape.maxLength,
            },
          }
        : { ok: false, message: t("common.error.reponseInattendue") };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** `F2.3`: the views, from the api that owns them. */
  const listPhotoViews = useCallback(async (): Promise<DoorResult<PhotoViews>> => {
    try {
      const answer = await api.request<unknown>("/api/v1/reference/photo-views", "get");
      // Checked and not cast, for the reason written on `listEquipment`: a door
      // answering another door's shape must become a refusal a person reads,
      // never a crash the boundary turns into a blank page.
      const shape = answer as Partial<PhotoViews> | null;
      return shape !== null &&
        Array.isArray(shape.recommended) &&
        Array.isArray(shape.other) &&
        typeof shape.otherView === "string"
        ? {
            ok: true,
            value: {
              recommended: shape.recommended,
              other: shape.other,
              otherView: shape.otherView,
            },
          }
        : { ok: false, message: t("common.error.reponseInattendue") };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** `F2.2`: the car's photographs, and which recommended views are missing. */
  const listPhotos = useCallback(
    async (
      vehicleId: string,
    ): Promise<DoorResult<{ photos: readonly VehiclePhoto[]; missingRecommended: readonly string[] }>> => {
      try {
        const answer = await api.request<VehiclePhotosAnswer>("/api/v1/me/vehicles/{id}/photos", "get", {
          params: { id: vehicleId },
        });
        return { ok: true, value: { photos: answer.data, missingRecommended: answer.missingRecommended } };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /**
   * 06 §6.3.3's three calls, as one gesture.
   *
   * **The PUT goes to S3 directly and never through `ApiClient`.** The store is
   * not this platform's api: it takes no bearer token, answers no 4.5.5
   * envelope, and the URL already carries its own credential in its signature.
   * A request built by the api client would send an `Authorization` header the
   * signature does not cover, which S3 refuses.
   */
  /**
   * `round F3`: **each of the three calls says which one it was.**
   *
   * The first cut wrapped all three in one `try` and one message. When the
   * store refused the PUT the person read « L'envoi de cette photo a échoué »
   * for a ticket that was never issued, and when the browser refused the PUT
   * they read « Failed to fetch » for a car that had already been created.
   *
   * Three steps, three sentences, and the store's STATUS in the one that is
   * about the store , because the api is not in that request's path and the
   * status is the only thing anybody will ever know about it.
   */
  const uploadPhoto = useCallback(
    async (input: { vehicleId: string; view: string; file: File }): Promise<DoorResult<{ id: string }>> => {
      let ticket: UploadTicket;
      try {
        ticket = await api.request<UploadTicket>("/api/v1/me/vehicles/{id}/photos/ticket", "post", {
          params: { id: input.vehicleId },
          body: { view: input.view, mimeType: input.file.type, sizeBytes: input.file.size },
        });
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.photoTicketEchoue")) };
      }

      /**
       * **The store's own request, and nothing of ours in it.**
       *
       * `credentials: "omit"` because a cross-origin request that carries them
       * is refused unless the response says `Access-Control-Allow-Credentials`,
       * and no object store ever does. The headers are the door's, VERBATIM ,
       * a header the browser spells differently is a 403 with no explanation ,
       * and there is no wrapper here on purpose: the api's surface header, its
       * bearer and its cookies have no business on this request and would each
       * add a preflight the bucket must be told to allow.
       *
       * `Content-Length` is not among them, and cannot be: it is a forbidden
       * header name, the browser sets it from the body, and a request that
       * tried to set it is ABORTED before it leaves.
       */
      let put: Response;
      try {
        put = await fetch(ticket.url, {
          method: "PUT",
          headers: ticket.headers,
          body: input.file,
          credentials: "omit",
          mode: "cors",
        });
      } catch (failure) {
        // A refusal by the browser itself. The reason is in the console and
        // never in this object; the person is told which step, and that the
        // car is safe.
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.photoStoreRefus")) };
      }
      if (!put.ok) {
        return {
          ok: false,
          message: `${t("dealer.vehicules.photoEnvoiEchoue")} (${String(put.status)})`,
        };
      }

      try {
        const attached = await api.request<PhotoAttachedAnswer>("/api/v1/me/vehicles/{id}/photos", "post", {
          params: { id: input.vehicleId },
          body: { ticketId: ticket.id, view: input.view },
        });
        return { ok: true, value: attached };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.photoConfirmEchoue")) };
      }
    },
    [api],
  );

  /** `A-DLR`, Arc B: the open requests on one car. */
  const readRequests = useCallback(
    async (vehicleId: string): Promise<DoorResult<DossierRequestListing>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/dossier-requests", "get", {
          params: { id: vehicleId },
        })) as { requests?: unknown; issued?: unknown } | null;
        if (!Array.isArray(answer?.requests))
          return { ok: false, message: t("common.error.reponseInattendue") };
        return {
          ok: true,
          value: {
            requests: answer.requests as readonly DossierRequest[],
            issued: Array.isArray(answer.issued) ? (answer.issued as readonly DossierProformas[]) : [],
          },
        };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `A-DLR`, Arc C: the dealership issues its proforma. No body: the price, the date and the number are not his to type. */
  const issueProforma = useCallback(
    async (input: { vehicleId: string; dossierId: string }): Promise<DoorResult<IssuedProforma>> => {
      try {
        const answer = (await api.request<unknown>(
          "/api/v1/me/vehicles/{id}/dossier-requests/{dossierId}/proforma/issue",
          "post",
          { params: { id: input.vehicleId, dossierId: input.dossierId } },
        )) as IssuedProforma | null;
        return answer === null
          ? { ok: false, message: t("common.error.reponseInattendue") }
          : { ok: true, value: answer };
      } catch (failure) {
        // The api's own sentence, naming each missing field.
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** The dealer's own filing of a dossier's proforma: the same three crossings, each saying which it was. */
  const uploadProforma = useCallback(
    async (input: {
      vehicleId: string;
      dossierId: string;
      file: File;
    }): Promise<DoorResult<{ code: string }>> => {
      let ticket: { id: string; url: string; headers: Record<string, string> } | null;
      try {
        ticket = (await api.request<unknown>(
          "/api/v1/me/vehicles/{id}/dossier-requests/{dossierId}/proforma/ticket",
          "post",
          {
            params: { id: input.vehicleId, dossierId: input.dossierId },
            body: { mimeType: input.file.type, sizeBytes: input.file.size },
          },
        )) as { id: string; url: string; headers: Record<string, string> } | null;
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.papiersTicketEchoue")) };
      }
      if (ticket === null) return { ok: false, message: t("common.error.reponseInattendue") };
      let put: Response;
      try {
        put = await fetch(ticket.url, {
          method: "PUT",
          headers: ticket.headers,
          body: input.file,
          credentials: "omit",
          mode: "cors",
        });
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.photoStoreRefus")) };
      }
      if (!put.ok) {
        return { ok: false, message: `${t("dealer.vehicules.papiersEnvoiEchoue")} (${String(put.status)})` };
      }
      try {
        await api.request<unknown>("/api/v1/me/vehicles/{id}/dossier-requests/{dossierId}/proforma", "post", {
          params: { id: input.vehicleId, dossierId: input.dossierId },
          body: { ticketId: ticket.id },
        });
        return { ok: true, value: { code: "factureProforma" } };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.papiersConfirmEchoue")) };
      }
    },
    [api],
  );

  /** `A-DLR`, Arc B (S4-6): the dealer's notices , read once the session stands, and after his own act of reading. */
  const [notices, setNotices] = useState<readonly DealerNotice[]>([]);
  /** The badge's number as the door serves it (`unread`), counted over ALL her rows; the panel holds at most fifty. */
  const [unread, setUnread] = useState(0);
  const loadNotices = useCallback(async (): Promise<void> => {
    try {
      const answer = await api.request<NotificationsAnswer>("/api/v1/me/notifications", "get");
      setUnread(answer.unread);
      setNotices(
        answer.notifications.map((row) => ({
          id: row.id,
          title: row.title,
          body: row.body,
          linkPath: row.linkPath,
          readAt: row.readAt,
        })),
      );
    } catch {
      // The bell keeps what it had; a notice that could not be fetched is never drawn as read.
    }
  }, [api]);
  useEffect(() => {
    if (status === "connecte") void loadNotices();
  }, [status, loadNotices]);
  const readNotice = useCallback(
    (id: string): void => {
      void api
        .request<ReadMarkAnswer>("/api/v1/me/notifications/{id}/read", "post", { params: { id } })
        .then(loadNotices)
        .catch(() => undefined);
    },
    [api, loadNotices],
  );

  const readAllNotices = useCallback((): void => {
    void api
      .request<ReadAllAnswer>("/api/v1/me/notifications/read", "post")
      .then(loadNotices)
      .catch(() => undefined);
  }, [api, loadNotices]);

  /** `A-DLR`, Arc A: the vehicle's papers, read for the card on the car's page. */
  const readPapers = useCallback(
    async (vehicleId: string): Promise<DoorResult<VehiclePapers>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/documents", "get", {
          params: { id: vehicleId },
        })) as VehiclePapers | null;
        if (answer === null || !Array.isArray(answer.documents)) {
          return { ok: false, message: t("common.error.reponseInattendue") };
        }
        return { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** The same three crossings as a photograph (ticket, the store's own PUT, confirmation), each saying which it was. */
  const attachPaper = useCallback(
    async (input: {
      vehicleId: string;
      kind: PaperKind;
      file: File;
      validUntil?: string;
    }): Promise<DoorResult<{ kind: PaperKind }>> => {
      let ticket: UploadTicket;
      try {
        ticket = await api.request<UploadTicket>("/api/v1/me/vehicles/{id}/documents/ticket", "post", {
          params: { id: input.vehicleId },
          body: { kind: input.kind, mimeType: input.file.type, sizeBytes: input.file.size },
        });
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.papiersTicketEchoue")) };
      }
      let put: Response;
      try {
        put = await fetch(ticket.url, {
          method: "PUT",
          headers: ticket.headers,
          body: input.file,
          credentials: "omit",
          mode: "cors",
        });
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.photoStoreRefus")) };
      }
      if (!put.ok) {
        return { ok: false, message: `${t("dealer.vehicules.papiersEnvoiEchoue")} (${String(put.status)})` };
      }
      try {
        await api.request<unknown>("/api/v1/me/vehicles/{id}/documents", "post", {
          params: { id: input.vehicleId },
          body: {
            kind: input.kind,
            ticketId: ticket.id,
            ...(input.validUntil === undefined ? {} : { validUntil: input.validUntil }),
          },
        });
        return { ok: true, value: { kind: input.kind } };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.papiersConfirmEchoue")) };
      }
    },
    [api],
  );

  const declareChassis = useCallback(
    async (vehicleId: string, chassisNumber: string): Promise<DoorResult<{ chassisNumber: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/chassis", "put", {
          params: { id: vehicleId },
          body: { chassisNumber },
        })) as { chassisNumber: string } | null;
        return answer === null
          ? { ok: false, message: t("common.error.reponseInattendue") }
          : { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("dealer.vehicules.chassisEchoue")) };
      }
    },
    [api],
  );

  const removePhoto = useCallback(
    async (vehicleId: string, photoId: string): Promise<DoorResult<void>> => {
      try {
        await api.request<unknown>("/api/v1/me/vehicles/{id}/photos/{photoId}", "delete", {
          params: { id: vehicleId, photoId },
        });
        return { ok: true, value: undefined };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `WF-DLR-02`'s publish. Its refusal is the api's own sentence. */
  const publishVehicle = useCallback(
    async (vehicleId: string): Promise<DoorResult<{ publishedAt: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/publish", "post", {
          params: { id: vehicleId },
        })) as { publishedAt: string } | null;
        return answer === null
          ? { ok: false, message: t("common.error.reponseInattendue") }
          : { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `WF-DLR-03`'s page. Out of scope answers exactly what absent answers. */
  const readVehicle = useCallback(
    async (id: string): Promise<DoorResult<VehicleDetail>> => {
      try {
        const answer = await api.request<DealerVehicleAnswer>("/api/v1/me/vehicles/{id}", "get", {
          params: { id },
        });
        // Derived, exactly as the list's rows are. This door has never sent a
        // boolean, and the page believed the field existed because a cast said
        // so , which is how « Mettre en ligne » came to stand on a live car.
        return { ok: true, value: withPublication(answer) };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /**
   * `P0-CAT-023`: « Mettre en ligne ».
   *
   * Every refusal is the api's own sentence , the price, the year, the
   * mileage, the plate already live, the reference already used. The form
   * renders what it is told and composes nothing: those rules are `catalog`'s,
   * and a second copy here would disagree the first time one changed.
   */
  const createVehicle = useCallback(
    async (input: Record<string, unknown>): Promise<DoorResult<{ id: string }>> => {
      try {
        const answer = await api.request<{ id: string }>("/api/v1/vehicles", "post", { body: input });
        return { ok: true, value: answer };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** The proposal the add panel fills in. A convenience; the door owns uniqueness. */
  const nextReference = useCallback(async (): Promise<DoorResult<string>> => {
    try {
      const answer = await api.request<{ stockReference?: unknown }>(
        "/api/v1/me/vehicles/next-reference",
        "get",
      );
      const proposed = answer.stockReference;
      if (typeof proposed !== "string") {
        return { ok: false, message: t("common.error.reponseInattendue") };
      }
      return { ok: true, value: proposed };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /**
   * `R64`, rule one: **the covered cities, from the door.** A typed city is
   * what let « Segou » and a Bamako quartier disagree; a chosen one cannot.
   */
  const listCities = useCallback(async (): Promise<DoorResult<readonly { id: string; name: string }[]>> => {
    try {
      const answer = await api.request<unknown>("/api/v1/cities", "get");
      const data = (answer as { data?: unknown } | null)?.data;
      if (!Array.isArray(data)) return { ok: false, message: t("common.error.reponseInattendue") };
      return { ok: true, value: data as readonly { id: string; name: string }[] };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** The places inside one city , Bamako's sixty-six, or the one zone elsewhere. */
  const listCityZones = useCallback(
    async (cityId: string): Promise<DoorResult<readonly { code: string; label: string }[]>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/cities/{cityId}/zones", "get", {
          params: { cityId },
        });
        const data = (answer as { data?: unknown } | null)?.data;
        if (!Array.isArray(data)) return { ok: false, message: t("common.error.reponseInattendue") };
        return { ok: true, value: data as readonly { code: string; label: string }[] };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /**
   * `P0-CAT-030`: the makes and models, read from the api and never transcribed.
   *
   * **The shape is checked rather than cast.** The panel puts these in two
   * dependent selects, and a body that arrived in another shape would render a
   * list of `undefined` rather than fail , which is the failure mode `F2`
   * booked when the equipment door first answered.
   */
  const listVehicleMakes = useCallback(async (): Promise<DoorResult<MakeReference>> => {
    try {
      const answer = (await api.request<unknown>("/api/v1/reference/vehicle-makes", "get")) as {
        data?: unknown;
        otherMake?: unknown;
      } | null;
      const data = answer?.data;
      if (!Array.isArray(data) || typeof answer?.otherMake !== "string") {
        return { ok: false, message: t("common.error.reponseInattendue") };
      }
      return {
        ok: true,
        value: {
          makes: data as MakeReference["makes"],
          otherMake: answer.otherMake,
        },
      };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /** `P0-CAT-030`: the gearbox and the fuel, key and label apart. */
  const listVehicleOptions = useCallback(async (): Promise<DoorResult<VehicleOptions>> => {
    try {
      const answer = (await api.request<unknown>("/api/v1/reference/vehicle-options", "get")) as {
        gearboxes?: unknown;
        fuels?: unknown;
      } | null;
      if (!Array.isArray(answer?.gearboxes) || !Array.isArray(answer.fuels)) {
        return { ok: false, message: t("common.error.reponseInattendue") };
      }
      return {
        ok: true,
        value: {
          gearboxes: answer.gearboxes as VehicleOptions["gearboxes"],
          fuels: answer.fuels as VehicleOptions["fuels"],
        },
      };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  /**
   * `P0-CAT-033`: the description edit.
   *
   * Every refusal is the api's own sentence , the engagement, the frozen
   * reference, the year window, the plate already live. The form renders what
   * it is told and composes nothing: those rules are `catalog`'s.
   */
  const editVehicle = useCallback(
    async (id: string, input: Record<string, unknown>): Promise<DoorResult<{ changed: boolean }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}", "patch", {
          params: { id },
          body: input,
        })) as { changed?: unknown } | null;
        return { ok: true, value: { changed: answer?.changed === true } };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** `P0-CAT-031`: the new asking price, and the two amounts the door answers. */
  const readAttention = useCallback(async (): Promise<DoorResult<readonly AttentionItem[]>> => {
    try {
      const answer = (await api.request<unknown>("/api/v1/me/attention", "get")) as {
        items?: unknown;
      } | null;
      if (!Array.isArray(answer?.items)) return { ok: false, message: t("common.error.reponseInattendue") };
      const items = (answer.items as unknown[]).flatMap((one): AttentionItem[] => {
        const row = one as Record<string, unknown> | null;
        if (
          (row?.["kind"] !== "miseADisposition" && row?.["kind"] !== "ecartPrix") ||
          typeof row["vehicleId"] !== "string"
        ) {
          return [];
        }
        return [
          {
            kind: row["kind"],
            vehicleId: row["vehicleId"],
            stockReference: typeof row["stockReference"] === "string" ? row["stockReference"] : "",
            title: typeof row["title"] === "string" ? row["title"] : "",
            since: typeof row["since"] === "string" ? row["since"] : "",
          },
        ];
      });
      return { ok: true, value: items };
    } catch (failure) {
      // `A4` part 2: a refused or failed read is SAID where it is drawn (« vos alertes »); the shell's dot simply is not worn.
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  const readPriceGap = useCallback(
    async (vehicleId: string): Promise<DoorResult<PriceGapView | null>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/price-gap", "get", {
          params: { id: vehicleId },
        })) as { gap?: Record<string, unknown> | null } | null;
        const gap = answer?.gap;
        if (gap === null || gap === undefined) return { ok: true, value: null };
        const reply = gap["answer"] as Record<string, unknown> | null | undefined;
        return {
          ok: true,
          value: {
            dossierReference: typeof gap["dossierReference"] === "string" ? gap["dossierReference"] : "",
            expertisedValue: Number(gap["expertisedValue"]),
            ceiling: Number(gap["ceiling"]),
            currentPrice: Number(gap["currentPrice"]),
            answer:
              reply === null || reply === undefined
                ? null
                : {
                    decision: reply["decision"] as "aligne" | "propose" | "maintenu",
                    price: Number(reply["price"]),
                    decidedAt: typeof reply["decidedAt"] === "string" ? reply["decidedAt"] : "",
                  },
          },
        };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  const answerPriceGap = useCallback(
    async (
      vehicleId: string,
      answer:
        { readonly kind: "align" | "maintain" } | { readonly kind: "propose"; readonly priceAmount: number },
    ): Promise<DoorResult<{ decision: string; previousAmount: number; priceAmount: number }>> => {
      try {
        const path =
          answer.kind === "align"
            ? "/api/v1/me/vehicles/{id}/price-gap/align"
            : answer.kind === "propose"
              ? "/api/v1/me/vehicles/{id}/price-gap/propose"
              : "/api/v1/me/vehicles/{id}/price-gap/maintain";
        const done = (await api.request<unknown>(path, "post", {
          params: { id: vehicleId },
          body: answer.kind === "propose" ? { priceAmount: answer.priceAmount } : {},
        })) as { decision?: unknown; previousAmount?: unknown; priceAmount?: unknown } | null;
        if (
          typeof done?.decision !== "string" ||
          typeof done.previousAmount !== "number" ||
          typeof done.priceAmount !== "number"
        ) {
          return { ok: false, message: t("common.error.reponseInattendue") };
        }
        return {
          ok: true,
          value: {
            decision: done.decision,
            previousAmount: done.previousAmount,
            priceAmount: done.priceAmount,
          },
        };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  const changePrice = useCallback(
    async (
      id: string,
      priceAmount: number,
    ): Promise<DoorResult<{ priceAmount: number; previousAmount: number }>> => {
      try {
        const body: VehiclePriceChangeBody = { priceAmount };
        const answer = vehiclePriceChangedAnswerReadSchema.safeParse(
          await api.request<unknown>("/api/v1/me/vehicles/{id}/price", "post", { params: { id }, body }),
        );
        if (!answer.success) return { ok: false, message: t("common.error.reponseInattendue") };
        return {
          ok: true,
          value: { priceAmount: answer.data.priceAmount, previousAmount: answer.data.previousAmount },
        };
      } catch (failure) {
        const refusal = priceRefusalFrom(failure);
        return {
          ...refusalOf(failure, t("common.error.connexionEchouee")),
          ...(refusal === undefined ? {} : { refusal }),
        };
      }
    },
    [api],
  );

  /** `OD-18`: the demand itself, recorded by a door. */
  const submitFeatureRequest = useCallback(
    async (input: {
      goal: string;
      missing: string;
      benefit: string;
      screenLabel: string;
    }): Promise<DoorResult<{ id: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/feature-requests", "post", {
          body: input,
        })) as { id?: unknown } | null;
        return typeof answer?.id === "string"
          ? { ok: true, value: { id: answer.id } }
          : { ok: false, message: t("common.error.reponseInattendue") };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** `round F6`, `OD-15`: « Suivi par », changed by the manager. */
  const assignVehicle = useCallback(
    async (id: string, userId: string): Promise<DoorResult<{ id: string; fullName: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/assignee", "post", {
          params: { id },
          body: { userId },
        })) as { assignee?: { id?: unknown; fullName?: unknown } } | null;
        const assignee = answer?.assignee;
        return typeof assignee?.id === "string" && typeof assignee.fullName === "string"
          ? { ok: true, value: { id: assignee.id, fullName: assignee.fullName } }
          : { ok: false, message: t("common.error.reponseInattendue") };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** `P0-CAT-032`: « Retirer du catalogue ». */
  const withdrawVehicle = useCallback(
    async (id: string): Promise<DoorResult<{ stage: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/withdraw", "post", {
          params: { id },
        })) as { stage?: unknown } | null;
        return typeof answer?.stage === "string"
          ? { ok: true, value: { stage: answer.stage } }
          : { ok: false, message: t("common.error.reponseInattendue") };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `P0-CAT-032`: « Remettre en ligne », which republishes in the same act. */
  /** `P1-DLW-048` (G-E1): the dossier list, through the dealer dossiers reader (`./dealer-dossiers.ts`, which lane B reuses). */
  const readDocuments = useCallback(
    (dossierId: string): Promise<DoorResult<DealerDocumentsAnswer>> =>
      readDealerDocumentsDoor(api, dossierId),
    [api],
  );
  const readDossierDetail = useCallback(
    (dossierId: string): Promise<DealerDossierDetailRead> => readDealerDossierDetail(api, dossierId),
    [api],
  );
  const readDossiers = useCallback(
    (): Promise<DoorResult<DealerDossiersReadAnswer>> => readDealerDossiers(api),
    [api],
  );

  /** `DEALER-PREQUAL`: `POST /api/v1/me/capacity-estimate` (`P1-SIM-017`, on main), a pure computation: the body is the contract's, the answer is left unparsed for the layer to read with the contract's lenient twin. Nothing is stored or logged here. */
  const estimateCapacity = useCallback<CapacityEstimateDoor>(
    (body) => api.request<unknown>("/api/v1/me/capacity-estimate", "post", { body }),
    [api],
  );

  /** `T-2`: the dealer's settlements, `GET /api/v1/me/settlements` (the family on main, 04's `/dealer/…` bound). */
  const readSettlements = useCallback(async (): Promise<DoorResult<readonly DealerSettlement[]>> => {
    try {
      const answer = await api.request<unknown>("/api/v1/me/settlements", "get");
      return { ok: true, value: dealerSettlementsFrom(answer) };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  const readAvailability = useCallback(
    async (vehicleId: string): Promise<DoorResult<unknown>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/me/vehicles/{id}/availability", "get", {
          params: { id: vehicleId },
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  const readReport = useCallback(
    async (vehicleId: string): Promise<DoorResult<DealerReportAnswer>> => {
      try {
        const answer = await api.request<DealerReportAnswer>("/api/v1/me/vehicles/{id}/rapport", "get", {
          params: { id: vehicleId },
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  const confirmAvailability = useCallback(
    async (vehicleId: string): Promise<DoorResult<unknown>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/me/vehicles/{id}/availability", "post", {
          params: { id: vehicleId },
          body: {},
          idempotencyKey: globalThis.crypto.randomUUID(),
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** WF-DLR-11 step 2: « Je confirme avoir reçu mes 90 % » , once, with an `Idempotency-Key` (`DECISION ADDED` 20). */
  const confirmReceipt = useCallback(
    async (
      settlementId: string,
      receivedOn: string,
      idempotencyKey?: string,
    ): Promise<DoorResult<unknown>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/me/settlements/{id}/confirm-receipt", "post", {
          params: { id: settlementId },
          body: { receivedOn },
          idempotencyKey: idempotencyKey ?? globalThis.crypto.randomUUID(),
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** WF-DLR-11 step 2: « Je n'ai rien reçu » , a claim to the chargé, the leg unconfirmed, the bon blocked. */
  const reportNonReceipt = useCallback(
    async (settlementId: string): Promise<DoorResult<unknown>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/me/settlements/{id}/report-non-receipt", "post", {
          params: { id: settlementId },
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** WF-DLR-11 step 5: the handover against the buyer's bon, `POST /api/v1/me/vehicles/{id}/handover`. */
  const handoverVehicle = useCallback(
    async (vehicleId: string, voucherReference: string): Promise<DoorResult<unknown>> => {
      try {
        const answer = await api.request<unknown>("/api/v1/me/vehicles/{id}/handover", "post", {
          params: { id: vehicleId },
          body: { voucherReference },
        });
        return { ok: true, value: answer };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  /** `T-2`, M0 (c): the dealership's own bank account , read, and entered by the responsable (a new version each time). */
  const readBankAccount = useCallback(async (): Promise<DoorResult<DealerBankAccount | null>> => {
    try {
      const answer = (await api.request<unknown>("/api/v1/me/dealership/bank-account", "get")) as {
        bankAccount?: DealerBankAccount | null;
      } | null;
      return { ok: true, value: answer?.bankAccount ?? null };
    } catch (failure) {
      return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
    }
  }, [api]);

  const writeBankAccount = useCallback(
    async (input: {
      bankName: string;
      holderName: string;
      accountNumber: string;
    }): Promise<DoorResult<DealerBankAccount>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/dealership/bank-account", "put", {
          body: input,
        })) as {
          bankAccount?: DealerBankAccount;
        } | null;
        return answer?.bankAccount === undefined
          ? { ok: false, message: t("common.error.reponseInattendue") }
          : { ok: true, value: answer.bankAccount };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  const relistVehicle = useCallback(
    async (id: string): Promise<DoorResult<{ stage: string }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/me/vehicles/{id}/relist", "post", {
          params: { id },
        })) as { stage?: unknown } | null;
        return typeof answer?.stage === "string"
          ? { ok: true, value: { stage: answer.stage } }
          : { ok: false, message: t("common.error.reponseInattendue") };
      } catch (failure) {
        return { ok: false, message: messageOf(failure, t("common.error.connexionEchouee")) };
      }
    },
    [api],
  );

  /** `round F5`: the resend, from the row the invitation is standing on. */
  const resendInvitation = useCallback(
    async (invitationId: string): Promise<DoorResult<{ delivered: boolean }>> => {
      try {
        const answer = (await api.request<unknown>("/api/v1/invitations/{id}/resend", "post", {
          params: { id: invitationId },
        })) as { delivered?: unknown } | null;
        // `delivered` absent means the api did not say, and the honest reading
        // is that it went , the invitation door beside it makes the same call
        // for the same reason.
        return { ok: true, value: { delivered: answer?.delivered !== false } };
      } catch (failure) {
        return refusalOf(failure, t("common.error.connexionEchouee"));
      }
    },
    [api],
  );

  const deconnecter = useCallback((): void => {
    // **Revoke first, then forget** , the console's own comment, and
    // `00-SPINE` 6.2's requirement: a logout that only cleared this tab would
    // leave the refresh cookie exchangeable by anyone holding it.
    void api.request("/api/v1/auth/logout", "post").catch(() => undefined);
    api.setAccessToken(null);
    setIdentite(null);
    setStatus("anonyme");
  }, [api]);

  return {
    status,
    identite,
    queryClient: queries.current,
    connecter,
    deconnecter,
    lireMoi,
    adopterSession,
    listTeam,
    invite,
    resendInvitation,
    assignVehicle,
    submitFeatureRequest,
    readDeactivation,
    deactivate,
    readDealership,
    readBalance,
    declareTopup,
    readSettlements,
    readDossiers,
    estimateCapacity,
    readDocuments,
    readDossierDetail,
    confirmReceipt,
    reportNonReceipt,
    handoverVehicle,
    readAvailability,
    readReport,
    confirmAvailability,
    readBankAccount,
    writeBankAccount,
    writeAddress,
    writeProformaDetails,
    listCities,
    listCityZones,
    listVehicles,
    readVehicleInterest,
    listEquipment,
    listPhotoViews,
    listVehicleMakes,
    listVehicleOptions,
    editVehicle,
    readAttention,
    readPriceGap,
    answerPriceGap,
    changePrice,
    withdrawVehicle,
    relistVehicle,
    listPhotos,
    uploadPhoto,
    readRequests,
    issueProforma,
    uploadProforma,
    notices,
    unreadNotices: unread,
    readNotice,
    readAllNotices,
    readPapers,
    attachPaper,
    declareChassis,
    removePhoto,
    publishVehicle,
    readVehicle,
    createVehicle,
    nextReference,
  };
}
